import React, { useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { PaginasService } from '../../../../service/paginas.service';
import { GcsService } from '../../../../service/gcs.service';
import { Pagina, TipoPagina } from '../../../../service/types/cursos.types';

interface PaginaFormProps {
  moduloId: string;
  initialValue?: Pagina;
  onSaved: (pagina: Pagina) => void;
  onCancel: () => void;
}

const TIPOS: { value: TipoPagina; label: string }[] = [
  { value: 'video', label: 'Video' },
  { value: 'documento', label: 'Documento' },
  { value: 'imagen', label: 'Imagen' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'descripcion', label: 'Descripción' },
  { value: 'test', label: 'Test' },
];

const ACCEPT_POR_TIPO: Partial<Record<TipoPagina, string>> = {
  video: 'video/*',
  imagen: 'image/*',
  documento: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx',
};

export default function PaginaForm({ moduloId, initialValue, onSaved, onCancel }: PaginaFormProps) {
  const [titulo, setTitulo] = useState(initialValue?.titulo ?? '');
  const [tipo, setTipo] = useState<TipoPagina>(initialValue?.tipo ?? 'descripcion');
  const [contenido, setContenido] = useState(initialValue?.contenido ?? '');
  const [urlArchivo, setUrlArchivo] = useState(initialValue?.urlArchivo ?? '');
  const [archivoNombre, setArchivoNombre] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [puntajeMinimo, setPuntajeMinimo] = useState(initialValue?.puntajeMinimo ?? 60);
  const [saving, setSaving] = useState(false);

  const isEditing = !!initialValue?.id;
  const esArchivo = tipo === 'video' || tipo === 'documento' || tipo === 'imagen';
  const esLink = tipo === 'youtube';

  const handleArchivoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendo(true);
    try {
      const { path } = await GcsService.upload(file);
      setUrlArchivo(path);
      setArchivoNombre(file.name);
    } catch {
      alert('No se pudo subir el archivo. Intente nuevamente.');
    } finally {
      setSubiendo(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titulo.trim()) {
      alert('Ingrese un título para la página');
      return;
    }
    if (esArchivo && !urlArchivo) {
      alert(`Suba el archivo de ${tipo} antes de guardar`);
      return;
    }
    if (esLink && !urlArchivo.trim()) {
      alert('Ingrese el link del video de YouTube');
      return;
    }
    setSaving(true);
    try {
      const dto = {
        titulo,
        tipo,
        contenido: contenido.trim() ? contenido : undefined,
        urlArchivo: esArchivo || esLink ? urlArchivo : undefined,
        puntajeMinimo: tipo === 'test' ? puntajeMinimo : undefined,
      };
      const pagina = isEditing
        ? await PaginasService.update(initialValue!.id, dto)
        : await PaginasService.create(moduloId, dto);
      onSaved(pagina);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit} className="border rounded p-3 mb-3 bg-light">
      <Form.Group className="mb-2">
        <Form.Label>Título de la página</Form.Label>
        <Form.Control type="text" value={titulo} onChange={(e) => setTitulo(e.target.value)} />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>Tipo</Form.Label>
        <Form.Select value={tipo} onChange={(e) => setTipo(e.target.value as TipoPagina)}>
          {TIPOS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>{tipo === 'descripcion' ? 'Contenido' : 'Descripción (opcional)'}</Form.Label>
        <Form.Control
          as="textarea"
          rows={4}
          value={contenido}
          onChange={(e) => setContenido(e.target.value)}
          placeholder={
            tipo === 'descripcion' ? undefined : 'Texto descriptivo que acompaña al archivo o test'
          }
        />
      </Form.Group>
      {esArchivo && (
        <Form.Group className="mb-2">
          <Form.Label>Archivo ({tipo})</Form.Label>
          {urlArchivo && !archivoNombre && (
            <div className="text-muted small mb-1">Ya hay un archivo subido para esta página.</div>
          )}
          <Form.Control
            type="file"
            accept={ACCEPT_POR_TIPO[tipo]}
            onChange={handleArchivoChange}
            disabled={subiendo}
          />
          {subiendo && (
            <div className="text-muted small mt-1">
              <Spinner animation="border" size="sm" /> Subiendo archivo...
            </div>
          )}
          {archivoNombre && !subiendo && (
            <div className="text-success small mt-1">Archivo listo: {archivoNombre}</div>
          )}
        </Form.Group>
      )}
      {esLink && (
        <Form.Group className="mb-2">
          <Form.Label>Link de YouTube</Form.Label>
          <Form.Control
            type="text"
            value={urlArchivo}
            onChange={(e) => setUrlArchivo(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
          />
        </Form.Group>
      )}
      {tipo === 'test' && (
        <>
          <Form.Group className="mb-2">
            <Form.Label>Puntaje mínimo para aprobar (%)</Form.Label>
            <Form.Control
              type="number"
              min={0}
              max={100}
              value={puntajeMinimo}
              onChange={(e) => setPuntajeMinimo(Number(e.target.value))}
            />
          </Form.Group>
          <p className="text-muted mb-2">
            Guarde la página y luego use "Editar preguntas" para configurar el test.
          </p>
        </>
      )}
      <div className="d-flex gap-2">
        <Button type="submit" variant="success" size="sm" disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar página'}
        </Button>
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
      </div>
    </Form>
  );
}
